/**
 * TeamMediaPage — /team/:teamId/media
 * ─────────────────────────────────────────────────────────────────
 * 2026-06-10. The team media gallery surfaced in the league module:
 * photos + short videos of games, Instagram/Snapchat-style. Files are
 * encrypted at rest under a per-team key (backend team-media.js); the
 * view/download endpoints are AUTHENTICATED, so the browser fetches
 * bytes with its bearer token and turns them into object URLs — no
 * public file URL ever exists.
 *
 *   • Responsive square grid; tap → full-screen lightbox with
 *     swipe/arrow navigation, caption, uploader, date.
 *   • Video plays inline in the lightbox; images render full-bleed.
 *   • Download button on every item (decrypted attachment).
 *   • Any team member can post; uploader or a coach can delete.
 *   • ?game=<id> filters to one game's media (the GamePage entry
 *     point links here with the game pre-selected).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Download, Trash2, Upload,
  Play, ImagePlus, Film,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTeamRole } from '../hooks/useTeamRole.js';

export default function TeamMediaPage() {
  const { teamId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const gameFilter = params.get('game') || null;
  const { user } = useAuth();
  const { isCoach } = useTeamRole(teamId);

  const [items, setItems] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Object-URL cache (mediaId → URL) so a thumb fetched once is reused
  // by the lightbox. Revoked on unmount.
  const urlCache = useRef(new Map());
  useEffect(() => () => {
    for (const u of urlCache.current.values()) URL.revokeObjectURL(u);
    urlCache.current.clear();
  }, []);

  const load = useCallback(async () => {
    try {
      const qs = gameFilter ? `?game_id=${encodeURIComponent(gameFilter)}` : '';
      const { data } = await api.get(`/teams/${teamId}/media${qs}`);
      setItems(data?.media || []);
    } catch (err) {
      setItems([]);
      if (err?.response?.status === 403) toast.error('Not a member of this team');
    }
  }, [teamId, gameFilter]);
  useEffect(() => { load(); }, [load]);

  // Fetch one media item's bytes (auth'd) → cached object URL.
  const fetchUrl = useCallback(async (m) => {
    if (urlCache.current.has(m.id)) return urlCache.current.get(m.id);
    const { data } = await api.get(`/teams/${teamId}/media/${m.id}/view`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    urlCache.current.set(m.id, url);
    return url;
  }, [teamId]);

  async function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-pick of the same file
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      if (!/^(image|video)\//.test(file.type)) {
        toast.error(`${file.name}: unsupported type`);
        continue;
      }
      const cap = file.type.startsWith('video/') ? 100 : 15;
      if (file.size > cap * 1024 * 1024) {
        toast.error(`${file.name}: exceeds ${cap} MB`);
        continue;
      }
      try {
        // Raw binary body — axios sends the File as-is with the
        // file's MIME as Content-Type. Caption/game ride in params.
        await api.post(`/teams/${teamId}/media`, file, {
          headers: { 'Content-Type': file.type },
          params: { ...(gameFilter ? { game_id: gameFilter } : {}) },
        });
        ok++;
      } catch (err) {
        toast.error(err?.response?.data?.error || `${file.name}: upload failed`);
      }
    }
    setUploading(false);
    if (ok > 0) { toast.success(`Posted ${ok} item${ok === 1 ? '' : 's'}`); load(); }
  }

  async function download(m) {
    try {
      const { data } = await api.get(`/teams/${teamId}/media/${m.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gameday-${m.id}.${m.ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error('Download failed');
    }
  }

  async function remove(m) {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await api.delete(`/teams/${teamId}/media/${m.id}`);
      const cached = urlCache.current.get(m.id);
      if (cached) { URL.revokeObjectURL(cached); urlCache.current.delete(m.id); }
      toast.success('Deleted');
      setLightboxIdx(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Delete failed');
    }
  }

  const canDelete = (m) => m.uploader_user_id === user?.id || isCoach;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => navigate(-1)} className="btn-ghost"
          style={{ padding: '8px 12px', fontSize: 12 }}>
          <ChevronLeft size={15} /> Back
        </button>
        <h1 className="section-title text-2xl" style={{ margin: 0, flex: 1 }}>
          📷 Team Media
        </h1>
        <button className="btn-ice" disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Upload size={15} /> {uploading ? 'Uploading…' : 'Add photos / video'}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple
          onChange={onPickFiles} style={{ display: 'none' }} />
      </div>

      {gameFilter && (
        // flexWrap — "Showing this game's media" + the "Show all
        // team media" button can be tight at ~390px; wrap instead of
        // overflowing (the header row above already does this).
        <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          Showing this game's media ·
          <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }}
            onClick={() => { params.delete('game'); setParams(params, { replace: true }); }}>
            Show all team media
          </button>
        </div>
      )}

      {items === null && <div style={{ color: 'var(--text-2)', padding: 30 }}>Loading…</div>}

      {items?.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '44px 24px', color: 'var(--text-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 12, opacity: 0.7 }}>
            <ImagePlus size={26} /> <Film size={26} />
          </div>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>No media yet</div>
          <div style={{ fontSize: 13 }}>
            Post photos and videos from your games — everyone on the team can see and download them.
          </div>
        </div>
      )}

      {items?.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 6,
        }}>
          {items.map((m, i) => (
            <Thumb key={m.id} media={m} fetchUrl={fetchUrl} onOpen={() => setLightboxIdx(i)} />
          ))}
        </div>
      )}

      {lightboxIdx != null && items?.[lightboxIdx] && (
        <Lightbox
          items={items}
          index={lightboxIdx}
          setIndex={setLightboxIdx}
          fetchUrl={fetchUrl}
          onClose={() => setLightboxIdx(null)}
          onDownload={download}
          onDelete={remove}
          canDelete={canDelete}
        />
      )}
    </div>
  );
}

// ─── Grid thumbnail ──────────────────────────────────────────────
// Images load their (auth'd) bytes lazily into an <img>. Videos show
// a poster card with a ▶ overlay — the actual clip only loads in the
// lightbox, so the grid stays light.
function Thumb({ media, fetchUrl, onOpen }) {
  const [url, setUrl] = useState(null);
  const ref = useRef(null);
  const isVideo = media.media_type === 'video';

  useEffect(() => {
    if (isVideo) return; // videos: no thumb fetch
    let cancelled = false;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        io.disconnect();
        fetchUrl(media).then(u => { if (!cancelled) setUrl(u); }).catch(() => {});
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => { cancelled = true; io.disconnect(); };
  }, [media, fetchUrl, isVideo]);

  return (
    <button ref={ref} type="button" onClick={onOpen}
      style={{
        position: 'relative', aspectRatio: '1 / 1', border: 'none',
        borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
        background: 'var(--surface-2)', padding: 0,
      }}>
      {isVideo ? (
        <div style={{
          width: '100%', height: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a2030, #0c1018)',
        }}>
          <span style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,255,255,0.92)', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Play size={20} color="#0b1220" fill="#0b1220" style={{ marginLeft: 2 }} />
          </span>
        </div>
      ) : url ? (
        <img src={url} alt={media.caption || ''} loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', background: 'var(--surface-2)' }} />
      )}
      {/* Type badge */}
      <span style={{
        position: 'absolute', top: 6, right: 6,
        padding: '2px 6px', borderRadius: 6, fontSize: 9, fontWeight: 800,
        background: 'rgba(0,0,0,0.55)', color: '#fff', letterSpacing: '0.05em',
      }}>
        {isVideo ? 'VIDEO' : 'PHOTO'}
      </span>
    </button>
  );
}

// ─── Full-screen lightbox ────────────────────────────────────────
function Lightbox({ items, index, setIndex, fetchUrl, onClose, onDownload, onDelete, canDelete }) {
  const m = items[index];
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    fetchUrl(m).then(u => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [m, fetchUrl]);

  const go = useCallback((delta) => {
    setIndex((i) => {
      const n = i + delta;
      return n < 0 ? items.length - 1 : n >= items.length ? 0 : n;
    });
  }, [items.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const dateStr = (() => {
    try { return new Date(m.created_at).toLocaleString(); } catch { return ''; }
  })();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        // Mobile fix — was 200, which sits BELOW BottomNav's z-index
        // 400 (components/shared/BottomNav.jsx). On mobile/Capacitor
        // the nav bar was rendering on top of this full-screen
        // lightbox, obscuring the bottom of the photo/video and its
        // controls. 500 matches the app-wide modal z-index convention
        // (GameScoreboardPage, GamePage, TeamPage, etc.), which is
        // already comfortably above the nav.
        zIndex: 500,
        background: 'rgba(4,6,12,0.94)',
        display: 'flex', flexDirection: 'column',
      }}>
      {/* Top bar */}
      <div onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', color: '#fff' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.caption || (m.media_type === 'video' ? 'Video' : 'Photo')}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            {m.uploader_name ? `${m.uploader_name} · ` : ''}{dateStr} · {index + 1} of {items.length}
          </div>
        </div>
        <button onClick={() => onDownload(m)} title="Download"
          style={iconBtn}><Download size={18} /></button>
        {canDelete(m) && (
          <button onClick={() => onDelete(m)} title="Delete"
            style={{ ...iconBtn, color: '#ff6b6b' }}><Trash2 size={18} /></button>
        )}
        <button onClick={onClose} title="Close" style={iconBtn}><X size={20} /></button>
      </div>

      {/* Media stage */}
      <div onClick={(e) => e.stopPropagation()}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '0 8px' }}>
        {items.length > 1 && (
          <button onClick={() => go(-1)} style={{ ...navBtn, left: 8 }}><ChevronLeft size={28} /></button>
        )}

        {!url && <div style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</div>}
        {/* maxHeight uses --mobile-h (dvh) rather than raw vh — inside
            the Capacitor WebView / mobile browsers, static vh can
            overshoot the actual visible viewport (keyboard, browser
            chrome), letting the media push past the top bar. */}
        {url && m.media_type === 'video' && (
          <video src={url} controls autoPlay playsInline
            style={{ maxWidth: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.78)', borderRadius: 8 }} />
        )}
        {url && m.media_type === 'image' && (
          <img src={url} alt={m.caption || ''}
            style={{ maxWidth: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.78)', objectFit: 'contain', borderRadius: 8 }} />
        )}

        {items.length > 1 && (
          <button onClick={() => go(1)} style={{ ...navBtn, right: 8 }}><ChevronRight size={28} /></button>
        )}
      </div>

      {/* Bottom breathing room + iPhone home-indicator clearance
          (matches BottomNav's paddingBottom convention) — the video's
          native controls / image's bottom edge otherwise sit flush
          against the gesture bar in Capacitor. */}
      <div style={{ height: 'calc(16px + env(safe-area-inset-bottom, 0px))' }} />
    </div>
  );
}

const iconBtn = {
  background: 'rgba(255,255,255,0.10)', border: 'none', color: '#fff',
  width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const navBtn = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
  width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
